import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './Hero.css';

function Hero() {
  const videoRef = useRef(null);
  const svgRef = useRef(null);
  const groupRef = useRef(null);

  // Animated infinity loop with glowing strands + traveling dots
  useEffect(() => {
    const svg = svgRef.current;
    const group = groupRef.current;
    if (!svg || !group) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const strandCount = 9;
    const paths = [];
    const dots = [];

    function infinityPoint(t, ax, ay, phase) {
      const s = Math.sin(t + phase);
      const denom = 1 + s * s;
      const x = ax * Math.cos(t + phase) / denom;
      const y = ay * Math.sin(t + phase) * Math.cos(t + phase) / denom;
      return { x, y };
    }

    // Create strands
    for (let i = 0; i < strandCount; i++) {
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('class', 'loop-path');
      p.setAttribute('stroke', i % 2 === 0 ? 'url(#loopGrad)' : 'url(#loopGrad2)');
      p.setAttribute('opacity', (0.2 + (i / strandCount) * 0.6).toFixed(2));
      p.setAttribute('stroke-width', (1.0 + (i / strandCount) * 1.2).toFixed(1));
      group.appendChild(p);
      paths.push(p);
    }

    // Create traveling dots (glowing particles along the loop)
    const dotCount = 5;
    for (let i = 0; i < dotCount; i++) {
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('r', (2 + Math.random() * 2.5).toFixed(1));
      c.setAttribute('fill', i % 2 === 0 ? '#c4b5fd' : '#e3b073');
      c.setAttribute('class', 'loop-dot');
      group.appendChild(c);
      dots.push({ el: c, speed: 0.3 + Math.random() * 0.5, offset: (i / dotCount) * Math.PI * 2 });
    }

    let mx = 0.5, my = 0.5;
    let targetMx = 0.5, targetMy = 0.5;

    const handlePointerMove = (e) => {
      const rect = svg.getBoundingClientRect();
      targetMx = (e.clientX - rect.left) / rect.width;
      targetMy = (e.clientY - rect.top) / rect.height;
    };

    const handlePointerLeave = () => {
      targetMx = 0.5;
      targetMy = 0.5;
    };

    svg.addEventListener('pointermove', handlePointerMove);
    svg.addEventListener('pointerleave', handlePointerLeave);

    let t0 = 0;
    let animId;

    function render() {
      mx += (targetMx - mx) * 0.05;
      my += (targetMy - my) * 0.05;
      const skew = (mx - 0.5) * 40;
      const lift = (my - 0.5) * -30;

      // Animate strands
      paths.forEach((p, i) => {
        const ax = 140 + i * 5;
        const ay = 85 + i * 4;
        const phaseWobble = reduceMotion ? 0 : Math.sin(t0 * 0.0008 + i * 0.7) * 0.18;
        const breathe = 1 + Math.sin(t0 * 0.0005 + i) * 0.03; // subtle breathing
        let d = '';
        const steps = 100;
        for (let s = 0; s <= steps; s++) {
          const t = (s / steps) * Math.PI * 2;
          const pt = infinityPoint(t, ax * breathe, ay * breathe, phaseWobble);
          const warpX = pt.x + skew * Math.sin(t * 1.5 + i);
          const warpY = pt.y + lift * Math.cos(t * 1.2 + i);
          d += (s === 0 ? 'M ' : 'L ') + (200 + warpX).toFixed(1) + ' ' + (200 + warpY).toFixed(1) + ' ';
        }
        d += 'Z';
        p.setAttribute('d', d);

        // Animate opacity for shimmer
        const shimmer = 0.25 + (i / strandCount) * 0.55 + Math.sin(t0 * 0.001 + i * 1.3) * 0.1;
        p.setAttribute('opacity', shimmer.toFixed(2));
      });

      // Animate traveling dots
      dots.forEach((dot) => {
        const t = (t0 * 0.001 * dot.speed + dot.offset) % (Math.PI * 2);
        const ax = 150;
        const ay = 92;
        const pt = infinityPoint(t, ax, ay, 0);
        dot.el.setAttribute('cx', (200 + pt.x).toFixed(1));
        dot.el.setAttribute('cy', (200 + pt.y).toFixed(1));

        // Pulse glow
        const pulse = 0.5 + Math.sin(t0 * 0.003 + dot.offset) * 0.5;
        dot.el.setAttribute('opacity', pulse.toFixed(2));
      });

      if (!reduceMotion) {
        t0 += 16;
        animId = requestAnimationFrame(render);
      }
    }

    render();

    return () => {
      svg.removeEventListener('pointermove', handlePointerMove);
      svg.removeEventListener('pointerleave', handlePointerLeave);
      if (animId) cancelAnimationFrame(animId);
      paths.forEach(p => { if (p.parentNode) p.parentNode.removeChild(p); });
      dots.forEach(d => { if (d.el.parentNode) d.el.parentNode.removeChild(d.el); });
    };
  }, []);

  return (
    <section className="hero" id="home">
      {/* Video Background */}
      <div className="hero__video-wrap">
        <video
          ref={videoRef}
          className="hero__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="hero__video-overlay" />
      </div>

      {/* Ambient glow effects */}
      <div className="ambient-glow" style={{ top: 0, left: 0 }} />
      <div
        className="ambient-glow"
        style={{
          bottom: 0,
          right: 0,
          background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, rgba(10,10,15,0) 60%)',
        }}
      />

      <div className="wrap hero__grid">
        <motion.div
          className="hero__content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 0.84, 0.44, 1] }}
        >
          <div className="eyebrow">ADAP8 INTELLIGENCE LLP</div>
          <h1>
            Your operations shouldn't adapt to software.
            <br />
            <em className="text-gradient">It should adapt to you.</em>
          </h1>
          <p className="hero__lead">
            We design and build custom, end-to-end software that molds itself around how your
            business already runs — flexible, automated, and personalized from day one.
          </p>
          <div className="hero__ctas">
            <a href="#contact" className="btn btn-primary">
              Start a conversation
            </a>
            <a href="#process" className="btn btn-ghost">
              See how we work
            </a>
          </div>
        </motion.div>

        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 0.84, 0.44, 1] }}
          aria-hidden="true"
        >
          <svg viewBox="0 0 400 400" ref={svgRef} id="hero-loop">
            <defs>
              <linearGradient id="loopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#e8e6f0" />
                <stop offset="45%" stopColor="#9b7bff" />
                <stop offset="100%" stopColor="#e3b073" />
              </linearGradient>
              <linearGradient id="loopGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#6c4cd6" />
                <stop offset="100%" stopColor="#9b7bff" />
              </linearGradient>
              <radialGradient id="dotGlow">
                <stop offset="0%" stopColor="#c4b5fd" stopOpacity="1" />
                <stop offset="100%" stopColor="#9b7bff" stopOpacity="0" />
              </radialGradient>
              {/* Glow filter for strands */}
              <filter id="loopGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Stronger glow for dots */}
              <filter id="dotGlowFilter" x="-300%" y="-300%" width="700%" height="700%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <g ref={groupRef} id="loop-strands" filter="url(#loopGlow)"></g>
          </svg>
        </motion.div>
      </div>
    </section>
  );
}

export default Hero;
